"use client";

import {
    useEffect,
    useRef,
} from "react";
import {
    usePathname,
} from "next/navigation";

const GOOGLE_ANALYTICS_ID =
    "G-8PFEXZMBR6";

const COOKIE_CONSENT_KEY =
    "cookieConsent";

const GOOGLE_ANALYTICS_SCRIPT_ID =
    "google-analytics-script";

const IS_PRODUCTION =
    process.env.NODE_ENV ===
    "production";

function isAnalyticsEnabled(
    consent
) {
    return (
        IS_PRODUCTION ||
        consent
            ?.developmentTracking ===
        true
    );
}

function shouldLog() {
    return (
        !IS_PRODUCTION &&
        window
            ?.googleAnalyticsDevelopmentTracking ===
        true
    );
}

function analyticsLog(
    message,
    details
) {
    if (
        typeof window ===
        "undefined" ||
        !shouldLog()
    ) {
        return;
    }

    if (
        details !== undefined
    ) {
        console.log(
            message,
            details
        );

        return;
    }

    console.log(
        message
    );
}

function analyticsError(
    message,
    error
) {
    if (
        typeof window ===
        "undefined" ||
        !shouldLog()
    ) {
        return;
    }

    console.error(
        message,
        error
    );
}

function getSavedConsent() {
    try {
        const savedConsent =
            window.sessionStorage.getItem(
                COOKIE_CONSENT_KEY
            );

        if (!savedConsent) {
            return null;
        }

        return JSON.parse(
            savedConsent
        );
    } catch (error) {
        analyticsError(
            "[GA] A mentett hozzájárulás nem olvasható.",
            error
        );

        return null;
    }
}

function deleteCookie(
    name
) {
    const hostname =
        window.location.hostname;

    const domains = [
        "",
        hostname,
        `.${hostname}`,
        ".strike.hu",
    ];

    domains.forEach(
        (domain) => {
            const domainPart =
                domain
                    ? `; domain=${domain}`
                    : "";

            document.cookie =
                `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
        }
    );
}

function removeGoogleAnalyticsCookies() {
    const cookieNames =
        document.cookie
            .split(";")
            .map(
                (cookie) =>
                    cookie
                        .split("=")[0]
                        .trim()
            )
            .filter(
                (cookieName) =>
                    cookieName ===
                    "_ga" ||
                    cookieName.startsWith(
                        "_ga_"
                    ) ||
                    cookieName.startsWith(
                        "_gid"
                    ) ||
                    cookieName.startsWith(
                        "_gat"
                    )
            );

    cookieNames.forEach(
        deleteCookie
    );
}

function initializeGoogleAnalytics(
    consent
) {
    if (
        typeof window ===
        "undefined" ||
        !isAnalyticsEnabled(
            consent
        )
    ) {
        return;
    }

    window
        .googleAnalyticsDevelopmentTracking =
        consent
            ?.developmentTracking ===
        true;

    if (
        window
            .googleAnalyticsInitialized
    ) {
        window
            .googleAnalyticsTrackingEnabled =
            true;

        if (
            typeof window.gtag ===
            "function"
        ) {
            window.gtag(
                "consent",
                "update",
                {
                    analytics_storage:
                        "granted",
                }
            );
        }

        return;
    }

    window
        .googleAnalyticsInitialized =
        true;

    window
        .googleAnalyticsTrackingEnabled =
        true;

    window.dataLayer =
        window.dataLayer || [];

    window.gtag =
        window.gtag ||
        function gtag() {
            window.dataLayer.push(
                arguments
            );
        };

    window.gtag(
        "js",
        new Date()
    );

    window.gtag(
        "consent",
        "update",
        {
            analytics_storage:
                "granted",
        }
    );

    window.gtag(
        "config",
        GOOGLE_ANALYTICS_ID,
        {
            send_page_view:
                true,
            debug_mode:
                !IS_PRODUCTION,
        }
    );

    const existingScript =
        document.getElementById(
            GOOGLE_ANALYTICS_SCRIPT_ID
        );

    if (
        existingScript
    ) {
        return;
    }

    const script =
        document.createElement(
            "script"
        );

    script.id =
        GOOGLE_ANALYTICS_SCRIPT_ID;

    script.async =
        true;

    script.src =
        `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;

    script.addEventListener(
        "load",
        () => {
            analyticsLog(
                "[GA] Google Analytics betöltve."
            );
        }
    );

    script.addEventListener(
        "error",
        (error) => {
            analyticsError(
                "[GA] Google Analytics betöltése sikertelen.",
                error
            );
        }
    );

    document.head.appendChild(
        script
    );
}

function disableGoogleAnalytics() {
    if (
        typeof window ===
        "undefined"
    ) {
        return;
    }

    window
        .googleAnalyticsTrackingEnabled =
        false;

    window
        .googleAnalyticsDevelopmentTracking =
        false;

    if (
        typeof window.gtag ===
        "function"
    ) {
        window.gtag(
            "consent",
            "update",
            {
                analytics_storage:
                    "denied",
            }
        );
    }

    removeGoogleAnalyticsCookies();

    analyticsLog(
        "[GA] Analitikai hozzájárulás visszavonva."
    );
}

function sendPageView(
    pathname
) {
    if (
        typeof window ===
        "undefined" ||
        typeof window.gtag !==
        "function" ||
        !window
            .googleAnalyticsInitialized ||
        !window
            .googleAnalyticsTrackingEnabled
    ) {
        return;
    }

    window.gtag(
        "event",
        "page_view",
        {
            page_path:
                pathname,
            page_location:
                window.location
                    .href,
            page_title:
                document.title,
            debug_mode:
                !IS_PRODUCTION,
        }
    );
}

export default function GoogleAnalytics() {
    const pathname =
        usePathname();

    const previousPathnameRef =
        useRef(null);

    useEffect(() => {
        const handleConsentSaved =
            (event) => {
                const nextConsent =
                    event.detail
                        ?.consent ||
                    null;

                if (
                    nextConsent
                        ?.analytics ===
                        true &&
                    isAnalyticsEnabled(
                        nextConsent
                    )
                ) {
                    initializeGoogleAnalytics(
                        nextConsent
                    );

                    return;
                }

                disableGoogleAnalytics();
            };

        const savedConsent =
            getSavedConsent();

        if (
            savedConsent
                ?.analytics ===
                true &&
            isAnalyticsEnabled(
                savedConsent
            )
        ) {
            initializeGoogleAnalytics(
                savedConsent
            );
        } else {
            disableGoogleAnalytics();
        }

        window.addEventListener(
            "cookie-consent-saved",
            handleConsentSaved
        );

        return () => {
            window.removeEventListener(
                "cookie-consent-saved",
                handleConsentSaved
            );
        };
    }, []);

    useEffect(() => {
        if (
            !pathname
        ) {
            return;
        }

        if (
            previousPathnameRef.current ===
            null
        ) {
            previousPathnameRef.current =
                pathname;

            return;
        }

        if (
            previousPathnameRef.current ===
            pathname
        ) {
            return;
        }

        previousPathnameRef.current =
            pathname;

        sendPageView(
            pathname
        );
    }, [
        pathname,
    ]);

    return null;
}